import React from 'react';
import { Button, Modal, Stack, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useCommonStore from 'store/common';
import { useGetReportReasonsQuery } from 'services';
import { getReportReasonKey, reasonOfReport } from 'utils';
import useTranslate from 'hooks/useTranslate';

const Report: React.FC<any> = () => {
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);
	const { data } = useGetReportReasonsQuery();
	const { translate } = useTranslate();

	const validationSchema = Yup.object({
		reason: Yup.string().required(
			translate('Validation.required', { field: translate('Validation.reason') }),
		),
		description: Yup.string().when('reason', {
			is: (val: string) => val === 'other',
			then: (schema) => schema.required(translate('Validation.required', { field: 'Description' })),
			otherwise: (schema) => schema.optional(),
		}),
	});

	const formik = useFormik({
		initialValues: {
			reason: '',
			description: '',
		},
		validationSchema,
		onSubmit: (values) => {
			const reportData = {
				...values,
				contentType: modalConfig?.contentType,
				contentId: modalConfig?.id,
			};
			modalConfig?.onClick(reportData);
		},
	});

	const renderReportOptions = () => {
		const reasonKey = getReportReasonKey(modalConfig?.contentType);
		const reasons = data?.[reasonKey.value] || [];

		return reasons.map((res: any) => (
			<Form.Check
				key={res?.title}
				inline
				label={res?.title}
				name="reason"
				type="radio"
				value={res?.value}
				checked={formik.values.reason === res?.value}
				onChange={formik.handleChange}
			/>
		));
	};

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		let inputValue = e.target.value;
		inputValue = inputValue.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
		formik.setFieldValue('description', inputValue);
	};

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={hideCommonModal}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Stack className="gap-2 gap-lg-5">
				<div className="mb-2 text-center">
					<h3 className="fw-bold mb-0">{translate('Modal.report')}</h3>
				</div>
				<Form onSubmit={formik.handleSubmit}>
					<Stack className="report-form">
						<div>
							<h5 className="fw-bold">
								{translate('Modal.whyYouAreReporting', {
									title: reasonOfReport(getReportReasonKey(modalConfig?.contentType).title),
								})}
							</h5>
							<Stack className="options">{renderReportOptions()}</Stack>
							{formik.touched.reason && formik.errors.reason && (
								<div className="text-danger">{formik.errors.reason}</div>
							)}
						</div>
						{formik?.values?.reason === 'other' && (
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.reasonOfReport')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={4}
									placeholder={translate('PlaceHolder.writeHere')}
									name="description"
									autoComplete="off"
									value={formik.values.description}
									onChange={handleDescriptionChange}
									isInvalid={formik.touched.description && !!formik.errors.description}
								/>
								<Form.Control.Feedback type="invalid">
									{formik.errors.description}
								</Form.Control.Feedback>
							</Form.Group>
						)}
						<Button type="submit" className="filled-btn rounded-pill lh-1">
							{translate('Modal.report')}
						</Button>
					</Stack>
				</Form>
			</Stack>
		</Modal.Body>
	);
};

export default Report;
